/**
 * Same-origin cross-tab communication via BroadcastChannel.
 *
 * BroadcastChannel is scoped to the same origin, so tabs on
 * sandbox.agrinous.com.au and app.agrinous.com.au will NOT see each
 * other's messages - which is exactly what we want.
 *
 * Note: programmatically focusing an arbitrary browser tab is not possible
 * without the tabs having a parent-child relationship (i.e. one opened via
 * window.open()). The modal therefore shows identifying info (path + open
 * since) so the user can find and close the duplicate tab themselves.
 */

const CHANNEL_NAME = "agrinous-tab-sync";
const TAB_ID_SESSION_KEY = "agrinousTabId";
const TAB_OPENED_AT_SESSION_KEY = "agrinousTabOpenedAt";

export enum TabMessageType {
  PING = "PING",
  PONG = "PONG",
}

/** Generate (or retrieve) a stable ID for this tab that persists across refreshes. */
export const getTabId = (): string => {
  let tabId = sessionStorage.getItem(TAB_ID_SESSION_KEY);
  if (!tabId) {
    tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(TAB_ID_SESSION_KEY, tabId);
    sessionStorage.setItem(TAB_OPENED_AT_SESSION_KEY, String(Date.now()));
  }
  return tabId;
};

/** Millisecond timestamp of when this tab was first opened (survives refreshes). */
export const getTabOpenedAt = (): number => {
  const stored = sessionStorage.getItem(TAB_OPENED_AT_SESSION_KEY);
  return stored ? parseInt(stored, 10) : Date.now();
};

export interface TabInfo {
  id: string;
  url: string;
  path: string;
  openedAt: number;
  livestockSaleId: string | null;
  activeRole: ActiveRole | null;
}

export type TabMessage =
  | { type: TabMessageType.PING; fromTabId: string }
  | {
      type: TabMessageType.PONG;
      fromTabId: string;
      url: string;
      path: string;
      openedAt: number;
      livestockSaleId: string | null;
      activeRole: ActiveRole | null;
    };

export const createTabChannel = (): BroadcastChannel =>
  new BroadcastChannel(CHANNEL_NAME);
